import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IoArrowBackOutline, IoCalendarOutline, IoTimeOutline } from "react-icons/io5";
import SubpageBanner from '../components/SubpageBanner';
import api from '../services/api';
import './NewsDetail.css';

export default function PromotionDetail() {
    const { t } = useTranslation();
    const { slug, id } = useParams();
    const navigate = useNavigate();
    const [promotionData, setPromotionData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Format date function
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('tr-TR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    // Format time function
    const formatTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleTimeString('tr-TR', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Get base URL for images
    const getImageBaseUrl = () => {
        const origin = window.location.origin;
        const isLocalOrTest = origin.toLowerCase().includes('local') || origin.toLowerCase().includes('test');
        return isLocalOrTest ? origin : origin;
    };

    // Fetch promotion detail
    useEffect(() => {
        const fetchPromotionDetail = async () => {
            try {
                setLoading(true);
                setError(null);
                
                const response = await api.getPromotionById(id);
                if (response.success && response.data) {
                    setPromotionData(response.data);
                } else {
                    setError(t('news.promotionError'));
                }
            } catch (err) {
                console.error('Error fetching promotion detail:', err);
                setError(t('news.promotionError'));
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchPromotionDetail();
        }
    }, [id]);

    if (loading) {
        return (
            <>
                <SubpageBanner
                    title={t('news.promotionsTitle')}
                    page={t('news.promotionsPage')}
                    path="/promotions"
                />
                <div className='news-detail-content'>
                    <div className="container">
                        <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
                            <div className="text-center">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">{t('news.loading')}</span>
                                </div>
                                <p className="mt-3">{t('news.loadingPromotions')}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    if (error) {
        return (
            <>
                <SubpageBanner
                    title={t('news.promotionsTitle')}
                    page={t('news.promotionsPage')}
                    path="/promotions"
                />
                <div className='news-detail-content'>
                    <div className="container">
                        <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
                            <div className="text-center">
                                <div className="alert alert-danger">
                                    <h4>{t('news.error')}</h4>
                                    <p>{error}</p>
                                    <button
                                        className="btn btn-primary"
                                        onClick={() => navigate('/promotions')}
                                    >
                                        {t('news.backToPromotions')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    if (!promotionData) {
        return (
            <>
                <SubpageBanner
                    title={t('news.promotionsTitle')}
                    page={t('news.promotionsPage')}
                    path="/promotions"
                />
                <div className='news-detail-content'>
                    <div className="container">
                        <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
                            <div className="text-center">
                                <div className="alert alert-warning">
                                    <h4>{t('news.error')}</h4>
                                    <p>{t('news.fetchError')}</p>
                                    <button
                                        className="btn btn-primary"
                                        onClick={() => navigate('/promotions')}
                                    >
                                        {t('news.backToPromotions')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <SubpageBanner
                title={t('news.promotionsTitle')}
                page={t('news.promotionsPage')}
                path="/promotions"
            />
            <div className='news-detail-content'>
                <div className="container">
                    {/* Back Button */}
                    <button
                        className="btn-back"
                        onClick={() => navigate('/promotions')}
                    >
                        <IoArrowBackOutline size={16} />
                        {t('news.backToPromotions')}
                    </button>

                    {/* Promotion Image with Title Overlay */}
                    {promotionData.imageUrl && (
                        <div className="news-detail-image">
                            <img
                                src={`${getImageBaseUrl()}/api/cms/images${promotionData.imageUrl}`}
                                alt={promotionData.title}
                            />
                            <h1 className="news-detail-title">
                                {promotionData.title}
                            </h1>
                        </div>
                    )}

                    {/* Promotion Meta */}
                    <div className="news-detail-meta">
                        <div className="d-flex align-items-center">
                            <IoCalendarOutline size={16} className="me-2" />
                            <span>{formatDate(promotionData.startDate)}</span>
                        </div>
                        <div className="d-flex align-items-center">
                            <IoTimeOutline size={16} className="me-2" />
                            <span>{formatTime(promotionData.startDate)}</span>
                        </div>
                    </div>

                    {/* Promotion Content */}
                    <div className="news-content-text"
                        dangerouslySetInnerHTML={{ 
                            __html: promotionData.content.replace(/\r\n/g, '<br>').replace(/\n/g, '<br>')
                        }}
                    />
                </div>
            </div>
        </>
    );
}
